/**
 * Administração da IA — chaves de API por escritório e margem dos créditos.
 *
 * A tela lista SÓ quem tem chave própria. Listar todos os escritórios com uma
 * coluna de status obrigava a ler linha a linha para descobrir o que já estava
 * configurado; aqui, estar na lista É ter chave. Quem não aparece usa a chave
 * da plataforma, sob a cota de créditos do plano — e o vínculo se faz pelo botão
 * do topo. Embaixo, a margem dos créditos (`MargemIaCard`).
 *
 * Por que o cadastro é de ADMIN e não do próprio escritório: enquanto o modelo
 * for "workspace dedicado com limite de gasto criado por nós", quem cadastra é
 * a plataforma. A chave em claro só existe no formulário: a lista mostra a
 * máscara (prefixo estrutural + 4 últimos).
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { fetchEscritoriosIa, gravarChaveIa, removerChaveIa, type EscritorioIa } from '../../api/adminIa';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Combobox } from '../../components/ui/combobox';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { EmptyState } from '../../components/ui/EmptyState';
import { ErrorState } from '../../components/ui/ErrorState';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { PageHeader } from '../../components/ui';
import { SearchInput } from '../../components/ui/search-input';
import { Spinner } from '../../components/ui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { useNotification } from '../../context/NotificationProvider';
import { classifyError, notifyMutationError } from '../../lib/errors';

import { MargemIaCard } from './MargemIaCard';

const CHAVE_QUERY = ['admin', 'ia', 'chaves'] as const;

/** Mínimo aceito pelo backend — evita ida ao servidor para um 422 previsível. */
const TAMANHO_MINIMO_CHAVE = 20;

function formatarReais(centavos: number): string {
  return (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatarData(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR');
}

function IaKeysPage() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const [busca, setBusca] = useState('');
  const [buscaDebounced, setBuscaDebounced] = useState('');
  // `null` = diálogo fechado; um escritório = trocar a chave dele; 'novo' = vincular.
  const [emEdicao, setEmEdicao] = useState<EscritorioIa | 'novo' | null>(null);
  const [alvo, setAlvo] = useState<string>(''); // escritório escolhido no vínculo
  const [chave, setChave] = useState('');
  const [aRemover, setARemover] = useState<EscritorioIa | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setBuscaDebounced(busca.trim()), 300);
    return () => clearTimeout(t);
  }, [busca]);

  // Busca server-side: filtrar no cliente veria só os primeiros N escritórios.
  const { data: lista, isLoading, error, refetch } = useQuery({
    queryKey: [...CHAVE_QUERY, 'com-chave', buscaDebounced || null],
    queryFn: () => fetchEscritoriosIa(buscaDebounced || undefined, true),
    // Mantém a lista anterior enquanto a busca nova vai ao servidor: sem isto a
    // tabela sumia e virava spinner a cada pausa de digitação.
    placeholderData: keepPreviousData,
  });

  // Todos os escritórios — só quando o diálogo de vínculo está aberto.
  const { data: todos, isLoading: carregandoTodos } = useQuery({
    queryKey: [...CHAVE_QUERY, 'todos'],
    queryFn: () => fetchEscritoriosIa(),
    enabled: emEdicao === 'novo',
  });

  const opcoes = useMemo(
    () =>
      (todos ?? []).map((e) => ({
        value: e.id,
        // Quem já tem chave aparece marcado: escolher esse escritório SUBSTITUI
        // a chave dele, e isso precisa estar visível antes do clique.
        label: e.chave_mascara ? `${e.nome} — já usa ${e.chave_mascara}` : e.nome,
        keywords: [e.nome],
      })),
    [todos],
  );

  const invalidar = () => queryClient.invalidateQueries({ queryKey: [...CHAVE_QUERY] });

  const fecharEdicao = () => {
    setEmEdicao(null);
    setAlvo('');
    setChave('');
  };

  const gravar = useMutation({
    mutationFn: ({ id, apiKey }: { id: string; apiKey: string }) => gravarChaveIa(id, apiKey),
    onSuccess: () => {
      showSuccess('Chave salva.');
      fecharEdicao();
      invalidar();
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  const remover = useMutation({
    mutationFn: (id: string) => removerChaveIa(id),
    onSuccess: () => {
      showSuccess('Chave removida. O escritório volta a usar a chave da plataforma.');
      setARemover(null);
      invalidar();
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  const visiveis = lista ?? [];
  const vinculando = emEdicao === 'novo';
  const escritorioAlvo = vinculando ? alvo : (emEdicao?.id ?? '');
  const podeSalvar = !!escritorioAlvo && chave.trim().length >= TAMANHO_MINIMO_CHAVE && !gravar.isPending;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Chaves de IA"
        subtitle="Escritório com chave própria tem o gasto de IA cobrado no provedor dele, sob o limite configurado lá. Os demais usam a chave da plataforma, sob a cota de créditos do plano."
        icon={<KeyRound className="h-5 w-5" />}
        actions={
          <Button onClick={() => setEmEdicao('novo')}>
            <Plus className="mr-2 h-4 w-4" />
            Vincular chave
          </Button>
        }
      />

      {visiveis.length > 0 && (
        <SearchInput value={busca} onChange={setBusca} placeholder="Buscar escritório" />
      )}

      {error ? (
        <ErrorState
          title="Não foi possível carregar a lista"
          description={classifyError(error).message}
          actions={[{ label: 'Tentar novamente', onClick: () => void refetch() }]}
        />
      ) : isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size={32} label="Carregando" />
        </div>
      ) : visiveis.length === 0 ? (
        <EmptyState
          icon={<KeyRound className="h-8 w-8" />}
          title={buscaDebounced ? 'Nenhum escritório com esse nome' : 'Nenhum escritório com chave própria'}
          description={
            buscaDebounced
              ? 'A busca cobre apenas escritórios que já têm chave.'
              : 'Todos usam a chave da plataforma. Vincule uma chave para isolar o gasto de um escritório no provedor dele.'
          }
          action={
            buscaDebounced ? undefined : { label: 'Vincular chave', onClick: () => setEmEdicao('novo') }
          }
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Escritório</TableHead>
                    <TableHead className="text-right">Membros</TableHead>
                    <TableHead>Chave</TableHead>
                    <TableHead>Atualizada em</TableHead>
                    <TableHead className="text-right">Gasto do mês</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visiveis.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.nome}</TableCell>
                      <TableCell className="text-right tabular-nums">{item.total_membros}</TableCell>
                      <TableCell>
                        {item.chave_legivel ? (
                          <Badge variant="secondary" className="font-mono">
                            {item.chave_mascara}
                          </Badge>
                        ) : (
                          <Badge variant="destructive">Ilegível — recadastre</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatarData(item.chave_atualizada_em)}
                      </TableCell>
                      {/* Gasto NESTA chave — é o que o escritório está pagando
                          no provedor dele neste mês. */}
                      <TableCell className="text-right tabular-nums">
                        {formatarReais(item.consumo_mes_centavos)}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEmEdicao(item);
                            setChave('');
                          }}
                        >
                          Trocar
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="ml-1"
                          aria-label={`Remover chave de ${item.nome}`}
                          onClick={() => setARemover(item)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <MargemIaCard />

      {/* Fechar por qualquer via (X, clique fora, Cancelar) DESCARTA a chave do
          state — ela não fica em memória até a próxima abertura do diálogo. */}
      <Dialog open={emEdicao !== null} onOpenChange={(aberto) => !aberto && fecharEdicao()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {vinculando ? 'Vincular chave da Anthropic a um escritório' : 'Trocar chave da Anthropic'}
            </DialogTitle>
            <DialogDescription>
              {vinculando
                ? 'A partir daí, as consultas de IA dos membros rodam nessa chave.'
                : (emEdicao as EscritorioIa | null)?.nome}
            </DialogDescription>
          </DialogHeader>

          {vinculando && (
            <div className="space-y-2">
              <Label htmlFor="escritorio-ia">Escritório</Label>
              <Combobox
                options={opcoes}
                value={alvo}
                onValueChange={setAlvo}
                placeholder="Selecione o escritório"
                searchPlaceholder="Buscar escritório..."
                emptyMessage={carregandoTodos ? 'Carregando...' : 'Nenhum escritório cadastrado.'}
                loading={carregandoTodos}
              />
            </div>
          )}

          <div className="space-y-2">
            {/* O provedor é explícito: hoje só a Anthropic é suportada, e uma
                chave de outro serviço só falharia na primeira consulta. */}
            <Label htmlFor="chave-ia">Chave de API da Anthropic</Label>
            <Input
              id="chave-ia"
              type="password"
              // "off" é ignorado pelo Chrome em campo de senha; "new-password"
              // é o que impede o gerenciador de oferecer/guardar a credencial.
              autoComplete="new-password"
              value={chave}
              onChange={(e) => setChave(e.target.value)}
              placeholder="sk-ant-..."
            />
            <p className="text-xs text-muted-foreground">
              Cole a chave gerada no console da Anthropic. Ela é guardada criptografada e,
              depois de salva, não dá para vê-la de novo — só o início e o final. A chave é
              exercitada na primeira consulta com IA do escritório.
            </p>
          </div>

          {!vinculando && (emEdicao as EscritorioIa | null)?.chave_mascara && (
            <Alert>
              <AlertDescription>
                A chave atual ({(emEdicao as EscritorioIa).chave_mascara}) será substituída.
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={fecharEdicao}>
              Cancelar
            </Button>
            <Button
              disabled={!podeSalvar}
              onClick={() => gravar.mutate({ id: escritorioAlvo, apiKey: chave.trim() })}
            >
              {gravar.isPending && <Spinner className="mr-2 text-inherit" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!aRemover}
        title="Remover a chave do escritório?"
        message={`${aRemover?.nome ?? ''} volta a consumir a chave da plataforma, sob a quota do plano.`}
        confirmLabel="Remover"
        confirmColor="error"
        loading={remover.isPending}
        onConfirm={() => aRemover && remover.mutate(aRemover.id)}
        onCancel={() => setARemover(null)}
      />
    </div>
  );
}

export default IaKeysPage;
